/* global google */
import { useQuery } from '@apollo/client'
import React, { useEffect, useState } from 'react'
import {
  GoogleMap,
  HeatmapLayer,
  Marker,
  Polygon,
  useLoadScript,
} from '@react-google-maps/api'
import { toastSweetAlert } from '../../Helpers/ToastSweetAlert'
import Loader from '../Spinner'
import { GET_ALL_JURISDICTIONS_AND_SUBS } from '../../../graphql/Catalog/Jurisdictions/jurisdictions'

const API_KEY = process.env.REACT_APP_API_KEY_GOOGLE_MAPS
const urlGeoJson = '/geomatica/zonas_tlajomulco.geojson'
const libraries = ['places', 'geometry', 'kml', 'visualization']

// Configuración de estilos y opciones del mapa
const DEFAULT_MAP_OPTIONS = {
  disableDefaultUI: true,
  zoomControl: true,
  fullscreenControl: true,
  streetViewControl: false,
}
const DEFAULT_MAP_STYLES = [
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
]
const DEFAULT_CENTER = { lat: 20.473623708878552, lng: -103.44747586737101 }

const HeatMapZones = ({
  mapContainerStyle = { width: '100%', height: '700px' },
  optionsGoogleMap = DEFAULT_MAP_OPTIONS,
  defaultCenterGoogleMap = DEFAULT_CENTER,
  stylesGoogleMap = DEFAULT_MAP_STYLES,
  reportsData = null,
  openHeatmapOptions = {
    radius: 25,
    dissipating: true,
    opacity: 1,
    maxIntensity: 100, // Ajusta la intensidad máxima
    gradient: [
      'rgba(255, 192, 203, 0)', // Rosa claro transparente (inicio)
      'rgba(255, 192, 203, 1)', // Rosa claro
      'rgba(255, 105, 180, 1)', // Rosa intenso (hot pink)
      'rgba(219, 112, 147, 1)', // Rosa medio (pale violet red)
      'rgba(199, 21, 133, 1)', // Fucsia oscuro (deep pink)
    ],
  },
  attendedHeatmapOptions = {
    radius: 25,
    dissipating: true,
    opacity: 1,
    maxIntensity: 100, // Ajusta la intensidad máxima
    gradient: [
      'rgba(255, 255, 178, 0)', // Transparente
      'rgba(255, 204, 102, 1)', // Amarillo pálido
      'rgba(255, 140, 0, 1)', // Naranja
      'rgba(255, 69, 0, 1)', // Rojo-Naranja
      'rgba(255, 0, 0, 1)', // Rojo
    ],
  },
}) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: API_KEY,
    libraries,
  })

  const [currentMap, setCurrentMap] = useState(null)
  const [geoZonesCoords, setGeoZonesCoords] = useState(null)
  const [zoomLevel, setZoomLevel] = useState(11)
  const [hoveredZone, setHoveredZone] = useState(null)
  const [subJurisdictions, setSubJurisdictions] = useState(null)
  const [heatmapData, setHeatmapData] = useState({
    open_reports: [],
    attended_reports: [],
  })
  const [heatmapKey, setHeatmapKey] = useState(0)

  //Optener el catalog_jurisdiction_sub
  const fetchSubs = useQuery(
    GET_ALL_JURISDICTIONS_AND_SUBS,
    { variables: { jurisdiction_id: 1 } },
    { fetchPolicy: 'no-cache' }
  )

  const onLoad = async (mapInstance) => {
    setCurrentMap(mapInstance)
  }

  const processHeadDataMap = async () => {
    const groupAndNormalize = (reports) => {
      const pointMap = new Map()

      for (const report of reports) {
        const lat = parseFloat(report?.AddressOfReport?.latitude)
        const lng = parseFloat(report?.AddressOfReport?.longitude)

        if (!isNaN(lat) && !isNaN(lng)) {
          const key = `${lat},${lng}`
          if (!pointMap.has(key)) {
            pointMap.set(key, { lat, lng, count: 1 })
          } else {
            pointMap.get(key).count += 1
          }
        }
      }

      const groupedPoints = Array.from(pointMap.values())

      const maxCount = Math.max(...groupedPoints.map((p) => p.count))
      const normalizedPoints = groupedPoints.map((p) => ({
        location: new google.maps.LatLng(p.lat, p.lng),
        weight: p.count / maxCount,
      }))

      return normalizedPoints
    }

    const points_open_reports = reportsData.open_reports.length
      ? groupAndNormalize(reportsData.open_reports)
      : []

    const points_attended_reports = reportsData.attended_reports.length
      ? groupAndNormalize(reportsData.attended_reports)
      : []

    setHeatmapKey((prevKey) => prevKey + 1)

    setHeatmapData({
      open_reports: points_open_reports,
      attended_reports: points_attended_reports,
    })
  }

  // const processHeadDataMap = async () => {
  //   const points_open_reports = []
  //   const points_attended_reports = []

  //   if (reportsData.open_reports.length > 0) {
  //     for await (const report of reportsData.open_reports) {
  //       const latitude = parseFloat(report?.AddressOfReport?.latitude)
  //       const longitude = parseFloat(report?.AddressOfReport?.longitude)
  //       if (!isNaN(latitude) && !isNaN(longitude)) {
  //         points_open_reports.push({
  //           location: new google.maps.LatLng(latitude, longitude),
  //           weight: 2,
  //         })
  //       }
  //     }
  //   }

  //   if (reportsData.attended_reports.length > 0) {
  //     for await (const report of reportsData.attended_reports) {
  //       const latitude = parseFloat(report?.AddressOfReport?.latitude)
  //       const longitude = parseFloat(report?.AddressOfReport?.longitude)
  //       if (!isNaN(latitude) && !isNaN(longitude)) {
  //         points_attended_reports.push({
  //           location: new google.maps.LatLng(latitude, longitude),
  //           weight: 2,
  //         })
  //       }
  //     }
  //   }

  //   setHeatmapKey((prevKey) => prevKey + 1)

  //   setHeatmapData({
  //     open_reports: points_open_reports,
  //     attended_reports: points_attended_reports,
  //   })
  // }

  const getProcessGeoJson = async () => {
    await currentMap.data.loadGeoJson(urlGeoJson, null, async (features) => {
      const dataZonesCoords = []

      const bounds = new google.maps.LatLngBounds() // Crear límites

      for await (const feature of features) {
        const zoneName = feature.getProperty('nombre')
        const coords = feature
          .getGeometry()
          .getArray()[0]
          .getArray()[0]
          .getArray()
          .map((item) => new google.maps.LatLng(item.lat(), item.lng()))

        const findZone = subJurisdictions.find((item) => item.name === zoneName)

        if (findZone) {
          dataZonesCoords.push({
            zone: {
              id: findZone.id,
              name: findZone.name,
              strokeColor: findZone.strokeColor,
              fillColor: findZone.fillColor,
            },
            coords,
            centroid: getPolygonCentroid(coords),
          })

          // Extender los límites con las coordenadas de la zona
          coords.forEach((coord) => bounds.extend(coord))
        }
      }
      setGeoZonesCoords(dataZonesCoords)
    })

    // Estilo GeoJSON
    currentMap.data.setStyle({ strokeWeight: 0.3 })
  }

  const handleAlertError = (message) => {
    toastSweetAlert({ mode: 'error', message })
  }

  useEffect(() => {
    if (!fetchSubs.loading) {
      const list = []
      fetchSubs.data?.getAllJurisdictionsAndSubs.map((jurisdiction) => {
        jurisdiction.SubsOfJurisdiction.map((sub) => {
          list.push({
            id: sub?.id,
            name: sub?.name,
            strokeColor: '#FFFFFF',
            fillColor: '#FFFFFF',
          })
        })
      })
      setSubJurisdictions(list)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchSubs.loading, fetchSubs.data, fetchSubs.error])

  useEffect(() => {
    if (currentMap && subJurisdictions?.length > 0) {
      getProcessGeoJson()
    }
  }, [currentMap, subJurisdictions])

  useEffect(() => {
    if (currentMap && reportsData?.total > 0) {
      processHeadDataMap()
    }
  }, [currentMap, reportsData])

  useEffect(() => {
    if (currentMap) {
      const zoomListener = currentMap.addListener('zoom_changed', () => {
        setZoomLevel(currentMap.getZoom())
      })
      return () => {
        google.maps.event.removeListener(zoomListener)
      }
    }
  }, [currentMap])

  if (loadError) {
    return (
      <div>
        <div className="alert alert-default-warning">Error al cargar mapa</div>
      </div>
    )
  }
  if (!isLoaded || !subJurisdictions) return <Loader size="2rem" />
  if (subJurisdictions.length === 0) {
    return (
      <div>
        <div className="alert alert-default-warning">
          No hay zonas registradas
        </div>
      </div>
    )
  }

  return (
    <GoogleMap
      mapContainerStyle={mapContainerStyle}
      center={defaultCenterGoogleMap}
      zoom={11}
      options={{
        ...optionsGoogleMap,
        styles: stylesGoogleMap,
        mapTypeControl: true,
        mapTypeControlOptions: {
          mapTypeIds: [
            google.maps.MapTypeId.ROADMAP,
            google.maps.MapTypeId.SATELLITE,
            google.maps.MapTypeId.HYBRID,
            google.maps.MapTypeId.TERRAIN,
          ],
        },
      }}
      onLoad={onLoad}
    >
      {/* Poligonos de las zonas  */}
      {geoZonesCoords?.map((item, index) => (
        <Polygon
          key={index}
          paths={item.coords}
          options={{
            strokeOpacity: 1,
            strokeColor: item.zone.strokeColor,
            fillColor: item.zone.fillColor,
            fillOpacity: 0.0,
            strokeWeight: 2,
            clickable: true,
            zIndex: 1,
          }}
          onMouseOver={() => setHoveredZone(index)}
          onMouseOut={() => setHoveredZone(null)}
        />
      ))}

      {/* Mostrar etiquetas de las zonas */}
      {geoZonesCoords?.map((item, index) => {
        return (
          <Marker
            key={index}
            position={item.centroid}
            label={{
              text: item.zone.name,
              className: 'bg-dark p-1 border border-dark rounded',
              fontSize: '10px',
            }}
            visible={zoomLevel <= 14 && hoveredZone === index}
            icon={{
              path: google.maps.SymbolPath.CIRCLE,
              scale: 0,
            }}
          />
        )
      })}

      {heatmapData.open_reports.length > 0 &&
        heatmapData.attended_reports.length == 0 && (
          <HeatmapLayer
            key={`HeatmapLayerOpen${heatmapKey}`}
            data={heatmapData.open_reports}
            options={openHeatmapOptions}
          />
        )}

      {heatmapData.attended_reports.length > 0 &&
        heatmapData.open_reports.length === 0 && (
          <HeatmapLayer
            key={`HeatmapLayerAttended${heatmapKey}`}
            data={heatmapData.attended_reports}
            options={attendedHeatmapOptions}
          />
        )}

      {heatmapData.open_reports.length > 0 &&
        heatmapData.attended_reports.length > 0 && (
          <>
            <HeatmapLayer
              key={`HeatmapLayerOpenAll${heatmapKey}`}
              data={heatmapData.open_reports}
              options={openHeatmapOptions}
            />

            <HeatmapLayer
              key={`HeatmapLayerAttendedAll${heatmapKey}`}
              data={heatmapData.attended_reports}
              options={attendedHeatmapOptions}
            />
          </>
        )}
    </GoogleMap>
  )
}

const getPolygonCentroid = (coords) => {
  let area = 0
  let lat = 0
  let lng = 0

  for (let i = 0; i < coords.length; i++) {
    const j = (i + 1) % coords.length
    const xi = coords[i].lat()
    const yi = coords[i].lng()
    const xj = coords[j].lat()
    const yj = coords[j].lng()
    const a = xi * yj - xj * yi
    area += a
    lat += (xi + xj) * a
    lng += (yi + yj) * a
  }

  area /= 2
  lat /= 6 * area
  lng /= 6 * area

  return { lat, lng }
}

export default HeatMapZones
